import React from "react";
import { Fieldset, Legend } from "@/components/fieldset";
import { Badge } from "@/components/badge";
import type { IUser } from "@/apis/users/getMe";
import GroupBadge from "@/components/GroupBadge";

interface StudentDetailsProps {
  user: IUser;
}

const StudentDetails: React.FC<StudentDetailsProps> = ({ user }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Define a detail item component for consistent styling
  const DetailItem = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {value || "-"}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <Fieldset>
        <Legend>Basic Information</Legend>
        <div className="mt-4 border-t border-b border-gray-100">
          <dl className="divide-y divide-gray-100">
            <DetailItem label="Name" value={user.name} />
            <DetailItem label="Email" value={user.email} />
            <DetailItem
              label="Status"
              value={
                user.status === "active" ? (
                  <Badge color="lime">Active</Badge>
                ) : (
                  <Badge color="zinc">Inactive</Badge>
                )
              }
            />
            <DetailItem
              label="Grade"
              value={
                user.is_graduated
                  ? "Graduated"
                  : user.grade === 0
                  ? "Not specified"
                  : `${user.grade}`
              }
            />
            <DetailItem label="School" value={user.school ?? "-"} />
            <DetailItem
              label="Group"
              value={<GroupBadge group={user.group} />}
            />
            <DetailItem label="Created" value={formatDate(user.created_at)} />
          </dl>
        </div>
      </Fieldset>

      {/* Contact Information */}
      <Fieldset>
        <Legend>Contact Information</Legend>
        <div className="mt-4 border-t border-b border-gray-100">
          <dl className="divide-y divide-gray-100">
            <DetailItem label="Student Phone" value={user.phone ?? "-"} />
            <DetailItem label="Parent Phone" value={user.parent_phone ?? "-"} />
          </dl>
        </div>
      </Fieldset>

      {/* Additional Information */}
      <Fieldset>
        <Legend>Additional Information</Legend>
        <div className="mt-4 border-t border-b border-gray-100">
          <dl className="divide-y divide-gray-100">
            <DetailItem
              label="Allow Early Submission"
              value={
                user.allow_early_submission ? (
                  <Badge color="lime">Enabled</Badge>
                ) : (
                  <Badge color="zinc">Disabled</Badge>
                )
              }
            />
            <DetailItem
              label="Memo"
              value={
                <div className="whitespace-pre-wrap">{user.memo || "-"}</div>
              }
            />
          </dl>
        </div>
      </Fieldset>
    </div>
  );
};

export default StudentDetails;
